import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { Button, Col, Form, FormGroup, Input, Label, Row } from "reactstrap";
import DataProtectionSoftwareService from "src/modules/data-protection/apis/DataProtectionSoftwareService";
import MultiSelect from "src/modules/data-protection/components/MultiSelect";
import {
  AI_ASSET_KINDS,
  ASSET_KIND_OPTIONS,
  CUSTOM_OPTION_VALUE,
  DEPLOYMENT_TYPES,
} from "../helpers/constants";
import useSoftwareCreationFunctionDefaults from "../hooks/useSoftwareCreationFunctionDefaults";
import useSoftwareFormOptions from "../hooks/useSoftwareFormOptions";
import { getDocumentErrors } from "../helpers/documents";
import { buildSoftwarePayload } from "../helpers/payload";
import { toIdList } from "../helpers/prefill";
import { isValidUrl } from "../helpers/url";
import AiSuggestion from "./AiSuggestion";
import DocumentsSection from "./DocumentsSection";
import FormSection from "./FormSection";
import HostingEvidenceNote from "./HostingEvidenceNote";
import WizardProgress from "./WizardProgress";

const CreateSoftwareFormStep = ({
  onBack,
  onCreated,
  organization,
  prefill,
  t,
}) => {
  const isOwnDevelopment = !!prefill?.isOwnDevelopment;
  const detection = prefill?.aiSystemDetection || prefill?.ai_system_info || null;
  const hostingEvidence = Array.isArray(prefill?.hostingEvidence)
    ? prefill.hostingEvidence
    : Array.isArray(prefill?.hosting_evidence)
    ? prefill.hosting_evidence
    : [];

  const [serviceName, setServiceName] = useState(
    prefill?.service || prefill?.softwareName || ""
  );
  const [assetKind, setAssetKind] = useState(prefill?.assetKind || "");
  const [deploymentType, setDeploymentType] = useState(prefill?.type || "");
  const [version, setVersion] = useState(prefill?.version || "");
  const [intendedPurpose, setIntendedPurpose] = useState("");
  const [selectedProvider, setSelectedProvider] = useState(
    prefill?.provider
      ? { label: prefill.provider, value: prefill.provider }
      : null
  );
  const [customProvider, setCustomProvider] = useState("");
  const [selectedOperatingSystem, setSelectedOperatingSystem] = useState(
    prefill?.operatingSystem
      ? {
          label: String(prefill.operatingSystem).trim(),
          value: String(prefill.operatingSystem).trim().toLowerCase(),
        }
      : null
  );
  const [customOperatingSystem, setCustomOperatingSystem] = useState("");
  const [selectedModules, setSelectedModules] = useState(
    (Array.isArray(prefill?.modules) ? prefill.modules : []).map((module) => ({
      label: module,
      value: module,
    }))
  );
  const [hostingLocations, setHostingLocations] = useState(
    Array.isArray(prefill?.hostingLocations) ? prefill.hostingLocations : []
  );
  const [nonEuHostingLocations, setNonEuHostingLocations] = useState(
    Array.isArray(prefill?.noneEUHostingLocations)
      ? prefill.noneEUHostingLocations
      : []
  );
  const [owner, setOwner] = useState("");
  const [selectedLegalEntities, setSelectedLegalEntities] = useState([]);
  const [subscription, setSubscription] = useState(false);
  const [notice, setNotice] = useState(prefill?.notice || "");
  const [documents, setDocuments] = useState(
    Array.isArray(prefill?.resources) ? prefill.resources : []
  );
  const [isAiSystem, setIsAiSystem] = useState(
    typeof detection?.is_ai_system === "boolean" ? detection.is_ai_system : null
  );
  const [errors, setErrors] = useState({});

  const options = useSoftwareFormOptions({
    organizationId: organization?.id,
    t,
  });

  const functionDefaults = useSoftwareCreationFunctionDefaults({
    selectedLegalEntities,
    t,
  });

  const sectionNumbers = useMemo(() => {
    const order = isOwnDevelopment
      ? ["details", "assessment", "hosting", "classification", "documents"]
      : [
          "details",
          "assessment",
          "hosting",
          "classification",
          "subscription",
          "documents",
        ];

    return order.reduce((accumulator, key, index) => {
      accumulator[key] = index + 1;
      return accumulator;
    }, {});
  }, [isOwnDevelopment]);

  const resolveSelectedName = (selected, customValue) => {
    if (!selected) return undefined;
    return selected.value === CUSTOM_OPTION_VALUE ? customValue : selected.label;
  };

  const createMutation = useMutation({
    mutationFn: async (software) =>
      DataProtectionSoftwareService.getInstance().create({
        assets: [software],
      }),
    onSuccess: (data) => {
      const pendingIds = data?.ai_processing_pending || [];
      if (!pendingIds.length) {
        toast(t("Software added successfully."), { type: "success" });
      }
      onCreated?.({
        isOwnDevelopment,
        hasPendingAi: pendingIds.length > 0,
      });
    },
    onError: (error) => {
      if (error?.response?.data?.error === "validation.software.already_used") {
        toast(t("This software has already been used."), { type: "error" });
        return;
      }

      toast(t("An error occurred while adding software."), { type: "error" });
    },
  });

  const validate = () => {
    const nextErrors = {};

    if (!serviceName.trim()) {
      nextErrors.serviceName = t("Software name is required.");
    }

    if (isOwnDevelopment) {
      if (!assetKind) {
        nextErrors.assetKind = t("Asset kind is required.");
      }
      if (!intendedPurpose.trim()) {
        nextErrors.intendedPurpose = t("Intended purpose is required.");
      }
      if (!prefill?.usage) {
        nextErrors.usage = t("Please select how this software is used.");
      }
    } else {
      if (!deploymentType) {
        nextErrors.deploymentType = t("Type is required.");
      }
      if (!selectedProvider) {
        nextErrors.provider = t("Provider is required.");
      } else if (
        selectedProvider.value === CUSTOM_OPTION_VALUE &&
        !customProvider.trim()
      ) {
        nextErrors.provider = t("Please write a custom provider.");
      }
      if (
        prefill?.softwareType !== "technical" &&
        !isValidUrl(prefill?.url || prefill?.providerUrl || "")
      ) {
        nextErrors.url = t("A valid public URL is required.");
      }
      if (isAiSystem !== true && isAiSystem !== false) {
        nextErrors.isAiSystem = t("Please choose Yes or No.");
      }
    }

    const documentErrors = getDocumentErrors(documents, t);

    if (documentErrors.length) {
      nextErrors.documents = documentErrors;
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) {
      return;
    }

    const moduleValues = selectedModules?.map((module) => module.label) || [];
    const documentValues = documents.filter((document) => document.link);
    const softwareUrl = prefill?.url || prefill?.publicUrl || prefill?.providerUrl || "";

    createMutation.mutate(
      buildSoftwarePayload({
        assetKind,
        detection,
        developmentUsageScope: prefill?.usage,
        documentValues,
        hostingEvidence,
        hostingLocationIds: toIdList(hostingLocations),
        intendedPurpose,
        isAiSystem,
        isOwnDevelopment,
        legalEntityValues: selectedLegalEntities
          ?.map((entity) => String(entity.id))
          .filter((id) => id && id !== "undefined" && id !== "null"),
        moduleValues,
        nonEuHostingLocationIds: toIdList(nonEuHostingLocations),
        notice,
        operatingSystem: resolveSelectedName(
          selectedOperatingSystem,
          customOperatingSystem
        ),
        owner,
        aiGovernanceResponsible: functionDefaults.aiGovernanceUser?.value,
        itSecurityResponsible: functionDefaults.itSecurityUser?.value,
        provider: resolveSelectedName(selectedProvider, customProvider),
        serviceName,
        softwareUrl,
        subscription,
        type: deploymentType,
        version,
      })
    );
  };

  const renderError = (key) =>
    errors[key] ? <small className="text-danger">{errors[key]}</small> : null;

  return (
    <Form
      className="software-create-form"
      onSubmit={(event) => event.preventDefault()}
    >
      <WizardProgress activeStep={3} t={t} />

      <FormSection
        index={sectionNumbers.details}
        title={t("Software Details")}
      >
        <Row>
          <Col sm="12" md="4">
            <FormGroup>
              <Label for="software-service-name">
                {t("Service Name")} <span className="text-danger">*</span>
              </Label>
              <Input
                id="software-service-name"
                value={serviceName}
                invalid={!!errors.serviceName}
                onChange={(event) => setServiceName(event.target.value)}
              />
              {renderError("serviceName")}
            </FormGroup>
          </Col>
          <Col sm="12" md="4">
            <FormGroup>
              <Label for="software-asset-kind">
                {isOwnDevelopment ? t("Asset Kind") : t("Type")}{" "}
                <span className="text-danger">*</span>
              </Label>
              {isOwnDevelopment ? (
                <Input
                  id="software-asset-kind"
                  type="select"
                  value={assetKind}
                  invalid={!!errors.assetKind}
                  onChange={(event) => {
                    const nextKind = event.target.value;
                    setAssetKind(nextKind);
                    if (AI_ASSET_KINDS.includes(nextKind)) {
                      setIsAiSystem(true);
                    }
                  }}
                >
                  <option value="">{t("Select")}</option>
                  {ASSET_KIND_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {t(option.label)}
                    </option>
                  ))}
                </Input>
              ) : (
                <Input
                  id="software-asset-kind"
                  type="select"
                  value={deploymentType}
                  invalid={!!errors.deploymentType}
                  onChange={(event) => setDeploymentType(event.target.value)}
                >
                  <option value="">{t("Select")}</option>
                  {DEPLOYMENT_TYPES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {t(option.label)}
                    </option>
                  ))}
                </Input>
              )}
              {renderError(isOwnDevelopment ? "assetKind" : "deploymentType")}
            </FormGroup>
          </Col>
          <Col sm="12" md="4">
            <FormGroup>
              <Label for="software-version">{t("Version")}</Label>
              <Input
                id="software-version"
                value={version}
                onChange={(event) => setVersion(event.target.value)}
              />
            </FormGroup>
          </Col>
        </Row>

        {isOwnDevelopment ? (
          <FormGroup>
            <Label for="software-intended-purpose">
              {t("Intended Purpose")} <span className="text-danger">*</span>
            </Label>
            <Input
              id="software-intended-purpose"
              type="textarea"
              rows="3"
              value={intendedPurpose}
              invalid={!!errors.intendedPurpose}
              onChange={(event) => setIntendedPurpose(event.target.value)}
            />
            {renderError("intendedPurpose")}
          </FormGroup>
        ) : (
          <Row>
            <Col sm="12" md="4">
              <FormGroup>
                <Label for="software-provider">
                  {t("Provider")} <span className="text-danger">*</span>
                </Label>
                <CreatableSelect
                  inputId="software-provider"
                  className="text-capitalize"
                  placeholder={`${t("Select")}...`}
                  value={selectedProvider}
                  options={options.providerOptions}
                  isLoading={options.isLoadingProviders}
                  onChange={(option) => {
                    setSelectedProvider(option);
                    setErrors((current) => ({ ...current, provider: "" }));
                  }}
                />
                {selectedProvider?.value === CUSTOM_OPTION_VALUE && (
                  <Input
                    className="mt-2"
                    placeholder={t("Custom provider")}
                    value={customProvider}
                    onChange={(event) => setCustomProvider(event.target.value)}
                  />
                )}
                {renderError("provider")}
              </FormGroup>
            </Col>
          </Row>
        )}
      </FormSection>

      <FormSection
        highlighted
        index={sectionNumbers.assessment}
        title={t("Assessment & AI")}
      >
        <div className="d-flex align-items-center gap-3">
          <FormGroup check className="mb-0">
            <Input
              id="software-is-ai-system-yes"
              type="radio"
              name="software-is-ai-system"
              checked={isAiSystem === true}
              onChange={() => {
                setIsAiSystem(true);
                setErrors((current) => ({ ...current, isAiSystem: "" }));
              }}
            />
            <Label check for="software-is-ai-system-yes">
              {t("Yes")}
            </Label>
          </FormGroup>
          <FormGroup check className="mb-0">
            <Input
              id="software-is-ai-system-no"
              type="radio"
              name="software-is-ai-system"
              checked={isAiSystem === false}
              onChange={() => {
                setIsAiSystem(false);
                setErrors((current) => ({ ...current, isAiSystem: "" }));
              }}
            />
            <Label check for="software-is-ai-system-no">
              {t("No")}
            </Label>
          </FormGroup>
        </div>
        {renderError("isAiSystem")}
        {detection && <AiSuggestion detection={detection} t={t} />}
      </FormSection>

      <FormSection
        index={sectionNumbers.hosting}
        title={t("Hosting & Deployment")}
      >
        <Row>
          <Col sm="12" md="4">
            <FormGroup>
              <Label for="software-hosting-location">
                {t("Hosting Location")}
              </Label>
              <CreatableSelect
                inputId="software-hosting-location"
                className="text-capitalize"
                placeholder={`${t("Select")}...`}
                isMulti
                value={hostingLocations}
                options={options.countryOptions}
                isLoading={options.isLoadingCountries}
                onChange={setHostingLocations}
              />
              <HostingEvidenceNote
                countryValues={hostingLocations.map((location) => location.value)}
                evidence={hostingEvidence}
                t={t}
              />
            </FormGroup>
          </Col>
          <Col sm="12" md="4">
            <FormGroup>
              <Label for="software-non-eu-hosting-location">
                {t("None EU Hosting Location")}
              </Label>
              <CreatableSelect
                inputId="software-non-eu-hosting-location"
                className="text-capitalize"
                placeholder={`${t("Select")}...`}
                isMulti
                value={nonEuHostingLocations}
                options={options.countryOptions}
                isLoading={options.isLoadingCountries}
                onChange={setNonEuHostingLocations}
              />
              <HostingEvidenceNote
                countryValues={nonEuHostingLocations.map(
                  (location) => location.value
                )}
                evidence={hostingEvidence}
                t={t}
              />
            </FormGroup>
          </Col>
          {!isOwnDevelopment && (
            <Col sm="12" md="4">
              <FormGroup>
                <Label for="software-operating-system">
                  {t("Operating System")}
                </Label>
                <CreatableSelect
                  inputId="software-operating-system"
                  className="text-capitalize"
                  placeholder={`${t("Select")}...`}
                  value={selectedOperatingSystem}
                  options={options.operatingSystemOptions}
                  isLoading={options.isLoadingOperatingSystems}
                  onChange={setSelectedOperatingSystem}
                />
                {selectedOperatingSystem?.value === CUSTOM_OPTION_VALUE && (
                  <Input
                    className="mt-2"
                    placeholder={t("Custom operating system")}
                    value={customOperatingSystem}
                    onChange={(event) =>
                      setCustomOperatingSystem(event.target.value)
                    }
                  />
                )}
              </FormGroup>
            </Col>
          )}
        </Row>
      </FormSection>

      <FormSection
        index={sectionNumbers.classification}
        title={t("Classification & Ownership")}
      >
        <Row>
          <Col sm="12" md="4">
            <FormGroup>
              <Label for="software-module">{t("Module")}</Label>
              <CreatableSelect
                inputId="software-module"
                className="text-capitalize"
                placeholder={`${t("Select")}...`}
                isMulti
                value={selectedModules}
                options={options.moduleOptions}
                isLoading={options.isLoadingModules}
                onChange={setSelectedModules}
              />
            </FormGroup>
          </Col>
          <Col sm="12" md="4">
            <FormGroup>
              <Label for="software-owner">{t("Owner")}</Label>
              <Input
                id="software-owner"
                type="select"
                className="text-capitalize"
                value={owner}
                onChange={(event) => setOwner(event.target.value)}
              >
                <option value="">{t("Select")}</option>
                {options.users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
          <Col sm="12" md="4">
            <FormGroup>
              <Label for="software-legal-entities">{t("Legal Entities")}</Label>
              <MultiSelect
                options={options.legalEntityOptions}
                selectedValues={selectedLegalEntities}
                onChange={setSelectedLegalEntities}
                placeholder={`${t("Select")}...`}
                isDisabled={options.isLoadingLegalEntities}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col sm="12" md="4">
            <FormGroup>
              <Label for="software-default-ai-governance">
                {functionDefaults.aiGovernanceTitle}
              </Label>
              <Select
                inputId="software-default-ai-governance"
                className="text-capitalize"
                placeholder={t("Select...")}
                isClearable
                isLoading={
                  functionDefaults.isFunctionsLoading ||
                  functionDefaults.isRoutingLoading
                }
                options={functionDefaults.aiGovernanceOptions}
                value={functionDefaults.aiGovernanceUser}
                onChange={functionDefaults.onAiGovernanceChange}
                isDisabled={
                  functionDefaults.isFunctionsLoading ||
                  (!functionDefaults.aiGovernanceOptions.length &&
                    !functionDefaults.aiGovernanceUser)
                }
              />
            </FormGroup>
          </Col>
          <Col sm="12" md="4">
            <FormGroup>
              <Label for="software-default-it-security">
                {functionDefaults.itSecurityTitle}
              </Label>
              <Select
                inputId="software-default-it-security"
                className="text-capitalize"
                placeholder={t("Select...")}
                isClearable
                isLoading={
                  functionDefaults.isFunctionsLoading ||
                  functionDefaults.isRoutingLoading
                }
                options={functionDefaults.itSecurityOptions}
                value={functionDefaults.itSecurityUser}
                onChange={functionDefaults.onItSecurityChange}
                isDisabled={
                  functionDefaults.isFunctionsLoading ||
                  (!functionDefaults.itSecurityOptions.length &&
                    !functionDefaults.itSecurityUser)
                }
              />
            </FormGroup>
          </Col>
        </Row>
      </FormSection>

      {!isOwnDevelopment && (
        <FormSection
          index={sectionNumbers.subscription}
          title={t("Subscription & Notes")}
        >
          <FormGroup check className="mb-3">
            <Input
              id="software-subscription"
              type="checkbox"
              checked={subscription}
              onChange={(event) => setSubscription(event.target.checked)}
            />
            <Label check for="software-subscription">
              {t("Has Subscription")}
            </Label>
          </FormGroup>
          <FormGroup>
            <Label for="software-notice">{t("Notice")}</Label>
            <Input
              id="software-notice"
              type="textarea"
              rows="3"
              value={notice}
              onChange={(event) => setNotice(event.target.value)}
            />
          </FormGroup>
        </FormSection>
      )}

      <FormSection index={sectionNumbers.documents} title={t("Documents")}>
        <DocumentsSection
          documents={documents}
            errors={errors.documents}
            onChange={(nextDocuments) => {
              setDocuments(nextDocuments);
              setErrors((current) => ({ ...current, documents: [] }));
            }}
          t={t}
        />
      </FormSection>

      <div className="software-create-form__actions">
        <Button outline color="primary" type="button" onClick={onBack}>
          {t("Back")}
        </Button>
        <div className="software-create-form__actions-right">
          <Button
            color="primary"
            type="button"
            disabled={createMutation.isLoading}
            onClick={handleSubmit}
          >
            {createMutation.isLoading ? `${t("Saving")}...` : t("Save")}
          </Button>
        </div>
      </div>
    </Form>
  );
};

export default CreateSoftwareFormStep;
